import { useTranslate } from "@opensea/next-translate"
import React from "react"
import { graphql, useFragment } from "react-relay"
import { CollectionMedia } from "@/components/common/media/CollectionMedia"
import type { ConfigureDrop_metadata$key } from "@/lib/graphql/__generated__/ConfigureDrop_metadata.graphql"
import type { ConfigureDrop_receipt$key } from "@/lib/graphql/__generated__/ConfigureDrop_receipt.graphql"
import { TxItem } from "../components/TxItem.react"
import { useTransactionUrl } from "../hooks/useTransactionUrl"
import type { TxState } from "../types"

type ConfigureDropProps = {
  receipt: ConfigureDrop_receipt$key | null | undefined
  metadata: ConfigureDrop_metadata$key
  state: TxState
  transactionHash: string | undefined
  nonce?: number
  createdDate?: string
}

export function ConfigureDrop({
  receipt: receiptKey,
  metadata: metadataKey,
  state,
  transactionHash,
  nonce,
  createdDate,
}: ConfigureDropProps) {
  const t = useTranslate("transactions")
  const transactionUrl = useTransactionUrl(transactionHash)

  const receipt = useFragment<ConfigureDrop_receipt$key>(
    graphql`
      fragment ConfigureDrop_receipt on TransactionReceiptType {
        transactionFee {
          unit
          symbol
          usd
        }
      }
    `,
    receiptKey,
  )

  const metadata = useFragment<ConfigureDrop_metadata$key>(
    graphql`
      fragment ConfigureDrop_metadata on TransactionMetadataType {
        collections {
          name
          ...CollectionMedia_collection
        }
      }
    `,
    metadataKey,
  )

  const collection = metadata.collections.at(0)

  const isComplete = receipt && transactionHash
  const isPending = !receipt && transactionHash

  return (
    <TxItem href={isPending ? undefined : transactionUrl} target="_blank">
      <TxItem.Avatar state={state}>
        <CollectionMedia collection={collection} />
      </TxItem.Avatar>
      <TxItem.Content>
        <TxItem.Title>
          {t("txItem.configureDrop.title", "Configure drop")}
        </TxItem.Title>
        <TxItem.Description>{collection?.name}</TxItem.Description>
      </TxItem.Content>
      {isComplete ? (
        <TxItem.Value
          symbol={receipt.transactionFee.symbol}
          unit={receipt.transactionFee.unit}
          usd={receipt.transactionFee.usd}
        />
      ) : isPending ? (
        <TxItem.Pending
          createdDate={createdDate}
          href={transactionUrl}
          nonce={nonce}
        />
      ) : (
        <TxItem.Requested />
      )}
    </TxItem>
  )
}
